// Sand recipe — plan §4.2 + Set3 §3.4: fbm base albedo, waves(domainWarp, cross-train, asymm)
// ripples over it, fbm grain, scatter sparkle. Immutable built-in root.
//
// SCALE CONTRACT (revised 2026-08-08 after a 4.5 m-wide viewport screenshot read as "tan commas
// on a grey field" instead of sand — see the three root causes below):
//   base    ~0.5 m fbm, COLOUR ONLY — the sand albedo everywhere.  Without this, the only
//           coloured layer was `ripples`, whose ramp only paints where the wave field is high,
//           so the troughs showed the underlying default grey.  A material must have a layer
//           that covers the WHOLE surface with its albedo (the dirt-recipe `base` precedent).
//   ripples ~4 cm wavelength — the DOMINANT relief (~4 mm), which is what makes sand read as
//           sand from overhead (D9: relief comes from the normal, not silhouette).  The domain
//           warp MUST be LOW-FREQUENCY relative to the wavelength (warpScale ~15x lambda,
//           warpAmount ~0.25x lambda): a warp at the feature's own scale displaces each point
//           independently of its neighbours and CHOPS crests into disconnected commas (exactly
//           what the screenshot showed at warpScale 0.05 / amount 0.025 vs lambda 0.04).
//           Whole crest segments must swing together for meander/merge/bifurcate to emerge.
//   grain   ~3 cm fbm, ~0.3 mm relief — the between-ripple texture.  Contributes COLOUR too
//           now: at 8 mm it was ~2 px at this zoom (pure aliasing, invisible as relief), and a
//           height-only layer that can't be resolved buys nothing.  Relief stays an order of
//           magnitude below the ripple relief so it never swamps the ripple shading.
//   sparkle ~2 cm scatter, ~0.4 mm — was 4 mm/0.05 mm, i.e. sub-pixel with a softness 2x its
//           own height (so the crest heightBlend gate was fully smeared and it read as a
//           uniform wash).  Now resolvable, and softness is a FRACTION of the height it gates.
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const SAND_RECIPE_ID = "sand";

export const sandRecipe = new MaterialRecipe({
	id: SAND_RECIPE_ID,
	name: "Sand",
	// 0.95 left no specular lobe at all, which contradicted the sparkle layer's whole rationale
	// (mica glints are a SPECULAR read).  Sand bulk is ~0.85; the specks ride the residual lobe.
	roughness: 0.85,
	specStrength: 0, // dry sand — no coherent highlight (see Recipe_Dirt)

	params: [
		// --- Base albedo (fix 1) ---
		new ParamSchema({key: "base_scale", label: "Base mottling scale (m)", kind: "scalar", min: 0.05, max: 20, default: 0.5}),
		new ParamSchema({key: "base_ramp", label: "Base sand ramp", kind: "ramp", default: colorRamp([
			{position: 0, color_str: `rgb(176,150,105)`}, // damp/shaded sand
			{position: 1, color_str: `rgb(214,190,143)`}, // dry sun-bleached sand
		])}),
		new ParamSchema({key: "ripple_wavelength", label: "Ripple spacing (m)", kind: "scalar", min: 0.005, max: 2, default: 0.25}),
		new ParamSchema({key: "ripple_angle", label: "Ripple angle (deg)", kind: "scalar", min: 0, max: 360, default: 105}),
		// Set3 §3.4.1 — the SECOND wave train, +20° off the main: crossing trains keep crests from
		// ever staying parallel (the "sand-coloured ropes" read).
		new ParamSchema({key: "ripple_crossAngle", label: "Cross-train angle (deg)", kind: "scalar", min: 0, max: 360, default: 125}),
		// Must stay LOW (a perturbation, not a partner). Mixed near-equally, the summed field only
		// peaks where BOTH trains peak, so pow() sharpening turns it into an intersection lattice of
		// isolated DASHES — a sinusoid alone can only make continuous bands. 0.45 did exactly that.
		new ParamSchema({key: "ripple_crossStrength", label: "Cross-train strength", kind: "scalar", min: 0, max: 1, default: 0.12}),
		// MUST stay incommensurate with 1.0: at an equal wavelength the two trains beat into a
		// rigid moire lattice (spacing lambda/(2 sin(delta/2)) ~ 11 cm at +20°) — that lattice, not
		// the ripples, was what the screenshot showed. 1.7 leaves no repeating beat structure.
		new ParamSchema({key: "ripple_crossWavelengthRatio", label: "Cross-train wavelength ratio", kind: "scalar", min: 0.1, max: 8, default: 2.3}),
		// Set3 §3.4.3 — the ASYMMETRIC crest profile: ~2.2 gives a steeper LEE side + a gentler
		// STOSS side, reading as a ridge rather than a rope.
		new ParamSchema({key: "ripple_asymm", label: "Crest asymmetry", kind: "scalar", min: 1, max: 8, default: 1}),
		// 1.0 = pure sinusoid. Any narrowing here reads as hard-edged parallel lines rather than
		// rounded sand ridges; the ASYMMETRY carries the "ridge" read instead.
		new ParamSchema({key: "ripple_sharpness", label: "Crest sharpness", kind: "scalar", min: 0.05, max: 8, default: 1}),
		// The ONLY source of irregularity here (a sinusoid is perfectly periodic). Amplitude must
		// reach ~1 lambda to make crests merge/bifurcate (0.35 lambda merely bent them), but the
		// SCALE must stay >> lambda or each point displaces alone and crests shatter into commas.
		new ParamSchema({key: "ripple_warpScale", label: "Ripple warp scale (m)", kind: "scalar", min: 0.02, max: 10, default: 10}),
		new ParamSchema({key: "ripple_warpAmount", label: "Ripple warp amount (m)", kind: "scalar", min: 0, max: 2, default: 1.5}),
		// Ripple crest ramp: NARROW spread around the base tone; albedo comes from the base layer.
		new ParamSchema({key: "sand_ramp", label: "Ripple ramp", kind: "ramp", default: colorRamp([
			{position: 0, color_str: `rgb(191,167,124)`},  // trough (shadowed)
			{position: 0.7, color_str: `rgb(193,169,126)`}, // flank (dominant)
			{position: 1, color_str: `rgb(195,171,128)`},   // crest (lit)
		])}),
		// Real ripple relief is ~1/15 of the spacing; 4 mm on a 4 cm wavelength was ~1/10, whose
		// steep flanks read as sharply-defined corrugation rather than soft sand.
		new ParamSchema({key: "ripple_height", label: "Ripple relief (m)", kind: "scalar", min: 0, max: 0.2, default: 0}),
		// Grain (fix 4): 8 mm was ~2 px at a 4.5 m-wide viewport — below the resolvable limit, so it
		// only ever aliased.  3 cm resolves as visible texture between the 4 cm ripples.  Octaves
		// dropped 3 → 2: the third octave (base/4 = 7.5 mm) is again near the sampling limit.
		new ParamSchema({key: "grain_scale", label: "Grain scale (m)", kind: "scalar", min: 0.001, max: 0.5, default: 0.001}),
		new ParamSchema({key: "grain_height", label: "Grain relief (m)", kind: "scalar", min: 0, max: 0.02, default: 0.001}),

		// Sparkle (fix 4): 4 mm spacing was ~1 px at this zoom — legally invisible.  2 cm gives a
		// speck every ~5 px, sparse enough (with presence) to never read as a lattice.
		new ParamSchema({key: "sparkle_density", label: "Sparkle spacing (m)", kind: "scalar", min: 0.001, max: 0.1, default: 0.02}),
		// Near-white specks. The colour is what reads as a mica glint under the specular lobe.
		new ParamSchema({key: "sparkle_ramp", label: "Sparkle ramp", kind: "ramp", default: colorRamp([
			{position: 0, color_str: `rgb(230,224,204)`},
			{position: 1, color_str: `rgb(255,252,242)`},
		])}),
		// 0.05 mm could not "stand above" a surface with 4 mm of ripple relief, so the heightBlend
		// crest-preference never engaged.  0.4 mm is a tenth of the ripple relief — small enough not
		// to rival it, large enough for the gate to actually discriminate crest from trough.
		new ParamSchema({key: "sparkle_height", label: "Sparkle relief (m)", kind: "scalar", min: 0, max: 0.005, default: 0.0001}),
		// MUST be a FRACTION of sparkle_height, not a multiple: the old 0.0001 was 2x the 0.00005
		// height, which smears the gate to a full-coverage wash — precisely the plain-`over`
		// behaviour the heightBlend was chosen to avoid.
		new ParamSchema({key: "sparkle_softness", label: "Sparkle blend softness", kind: "scalar", min: 0.00001, max: 0.01, default: 0.002}),
		// Set3 §4.1 — sparkle presence: sparser now that each speck is resolvable (0.75 of a 2 cm
		// lattice would read as a regular stipple).
		new ParamSchema({key: "sparkle_presence", label: "Sparkle presence", kind: "scalar", min: 0.05, max: 1, default: 0.35}),
	],
	layers: [
		// Base: the sand ALBEDO, covering the whole surface. Colour only — metres-scale relief
		// would dominate the height chain and gate the sparkle heightBlend by macro noise instead
		// of by the ripples (the dirt-recipe `base` lesson). Macro undulation is the TERRAIN's job.
		new MaterialLayer({
			id: "base",
			primitive: "fbm",
			args: {
				scale: {ref: "base_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "base_ramp"},
				heightScale: 0,
			},
			blend: "over",
			contributes: "color",
		}),
		new MaterialLayer({
			id: "ripples",
			primitive: "waves",
			args: {
				wavelength: {ref: "ripple_wavelength"},
				angle: {ref: "ripple_angle"},
				sharpness: {ref: "ripple_sharpness"}, // mild crest narrowing; the ASYMMETRY carries the "ridge" read
				crossAngle: {ref: "ripple_crossAngle"},
				crossStrength: {ref: "ripple_crossStrength"},
				crossWavelengthRatio: {ref: "ripple_crossWavelengthRatio"},
				asymm: {ref: "ripple_asymm"},
				warpScale: {ref: "ripple_warpScale"},
				warpOctaves: 3,
				warpAmount: {ref: "ripple_warpAmount"},
				colorRamp: {ref: "sand_ramp"},
				heightScale: {ref: "ripple_height"},
			},
			blend: "over",
			contributes: "both",
		}),
		// Grain: relief ONLY. As a colour contributor with blend "over" it became the LAST
		// colour-writing layer, so its pale ramp replaced both the base albedo and the ripple crest
		// ramp outright — the desaturated wash that hid the ripples. Scale stays resolvable (3 cm).
		new MaterialLayer({
			id: "grain",
			primitive: "fbm",
			args: {
				scale: {ref: "grain_scale"},
				octaves: 2,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				heightScale: {ref: "grain_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		// Sparkle: heightBlend so a speck only shows where it stands above the accumulated surface —
		// i.e. preferentially on the ripple CRESTS, which is where real sand glints. A plain `over`
		// would paint specks uniformly, including down in the shadowed troughs.
		new MaterialLayer({
			id: "sparkle",
			primitive: "scatter",
			args: {
				density: {ref: "sparkle_density"},
				shape: 0,
				aspect: 1,
				// Rotation jitter is a no-op on a round, unit-aspect speck (the dirt-recipe note).
				rotJitter: 0,
				// Full placement jitter: with a disc there is no rotation to collide with, and 0.7 kept
				// every speck inside a visible 0.7-cell box (the lattice stayed legible as a grid).
				placeJitter: 1,
				presence: {ref: "sparkle_presence"},
				colorRamp: {ref: "sparkle_ramp"},
				height: {ref: "sparkle_height"},
				colorJitterSeed: 7,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "sparkle_softness"}},
			contributes: "both",
		}),
	],
});